import { getStoredAudioVideoSettings } from '../../core/utils';
import { ZoomLocalPreviewAudioDelegator } from '../room/participant/media/LocalPreviewAudioDelegator';
import { ZoomLocalPreviewVideoDelegator } from '../room/participant/media/LocalPreviewVideoDelegator';
import { VideoClient } from '../types';
import { handleError } from './handleError';
import { getVirtualBackgroundArg, promptAudioVideoPermission } from './media';

type InitializePreviewResult =
  | {
      success: true;
      previewAudioDelegator: ZoomLocalPreviewAudioDelegator | null;
      previewVideoDelegator: ZoomLocalPreviewVideoDelegator | null;
    }
  | {
      success: false;
      previewAudioDelegator: null;
      previewVideoDelegator: null;
      error: Error;
    };

/**
 * Requests media permissions and prepares audio/video for waiting room preview.
 * Returns placeholder delegators in disabled state if permissions are cancelled.
 */
export async function initializePreviewAudioVideo(
  videoClient: VideoClient,
  isVirtualBackgroundSupported?: boolean,
): Promise<InitializePreviewResult> {
  const failureResult: Omit<Extract<InitializePreviewResult, { success: false }>, 'error'> = {
    success: false,
    previewAudioDelegator: null,
    previewVideoDelegator: null,
  };

  const { audio, video } = await promptAudioVideoPermission();

  if (!audio.permissionGranted || !video.permissionGranted) {
    return {
      ...failureResult,
      error: new Error('Preview audio/video device permission error'),
    };
  }

  const previewAudioDelegator =
    (await initializePreviewAudio({
      targetAudioInputDeviceId: audio.deviceId,
    }).catch((error) =>
      handleError('[ZoomVideoSDKAdapter]: initializePreviewAudioVideo->initializePreviewAudio', error),
    )) ?? null;

  const previewVideoDelegator =
    (await initializePreviewVideo({
      videoClient,
      targetVideoInputDeviceId: video.deviceId,
      isVirtualBackgroundSupported,
    }).catch((error) =>
      handleError('[ZoomVideoSDKAdapter]: initializePreviewAudioVideo->initializePreviewVideo', error),
    )) ?? null;

  if (!previewAudioDelegator && !previewVideoDelegator) {
    return {
      ...failureResult,
      error: new Error('Preview audio/video initialization error'),
    };
  }

  return {
    success: true,
    previewAudioDelegator,
    previewVideoDelegator,
  };
}

export async function initializePreviewAudio({
  targetAudioInputDeviceId,
}: {
  /**
   * Attempts to use the provided audio input device ID
   */
  targetAudioInputDeviceId?: string;
} = {}): Promise<ZoomLocalPreviewAudioDelegator | null> {
  const deviceId = targetAudioInputDeviceId
    ? targetAudioInputDeviceId
    : (await promptAudioVideoPermission()).audio.deviceId;
  if (!deviceId) {
    return null;
  }
  const { isAudioEnabled } = getStoredAudioVideoSettings();

  try {
    return await ZoomLocalPreviewAudioDelegator.create(deviceId, isAudioEnabled);
  } catch (error) {
    handleError('[ZoomVideoSDKAdapter]: initializePreviewAudio', error);
    return null;
  }
}

export async function initializePreviewVideo({
  videoClient,
  targetVideoInputDeviceId,
  isVirtualBackgroundSupported,
}: {
  videoClient: VideoClient;
  /**
   * Attempts to use the provided video input device ID
   */
  targetVideoInputDeviceId?: string;
  isVirtualBackgroundSupported?: boolean;
}): Promise<ZoomLocalPreviewVideoDelegator | null> {
  const deviceId = targetVideoInputDeviceId
    ? targetVideoInputDeviceId
    : (await promptAudioVideoPermission()).video.deviceId;
  if (!deviceId) {
    return null;
  }

  const { isVideoEnabled, backgroundType, backgroundIndex } = getStoredAudioVideoSettings();
  const virtualBackground = await getVirtualBackgroundArg(
    backgroundType,
    backgroundIndex,
    isVirtualBackgroundSupported,
  );

  return new ZoomLocalPreviewVideoDelegator(deviceId, isVideoEnabled, videoClient, virtualBackground);
}
